import React from "react";
import styled from "styled-components";
import { Card } from "../Card";
import { primaryColor } from "../colors";
import { mobileQuery } from "../MediaQuery";
import { MySlider } from "../MySlider";
import { ResponsiveLayout } from "../ResponsiveLayout";
import { SizedBox } from "../SizedBox";
import { Header3, Paragraph1 } from "../typography";
import { Section } from "./Section";

interface BenefitInfo {
  title: string;
  content: string;
}

interface Benefit {
  image: string;
  info: BenefitInfo;
}

const benefits: Array<Benefit> = [
  {
    image: "flexible.svg",
    info: {
      title: "Flexible solution",
      content:
        "Web app? Mobile app? Module for your existing app? Choose what suites you the most!",
    },
  },
  {
    image: "branding.svg",
    info: {
      title: "Branding",
      content: "Connect & engage, boost NPS & retention.",
    },
  },
  {
    image: "longevity.svg",
    info: {
      title: "Longevity tracking & content",
      content:
        "Instant longevity check-up, live health tracking, 11 health paths. Acquiring, connecting and engaging policyholders.",
    },
  },
  {
    image: "rewards.svg",
    info: {
      title: "Game rewards",
      content:
        "Rewards makes customers happy. Use predefined badges, coins or add and define your rewards.",
    },
  },
];

export const CustomizedSolutionsSection: React.FC = () => {
  return (
    <Section>
      <Layout>
        <Header3 color={primaryColor} center>
          Customized Solution for Health Insurers
        </Header3>

        <SizedBox desktopHeight={24} mobileHeight={16} />

        <ContentLayout>
          <BenefitsList>
            {benefits.map((benefit, i) => {
              return (
                <React.Fragment key={`benefit-${i}`}>
                  <BenefitBox
                    image={benefit.image}
                    title={benefit.info.title}
                    description={benefit.info.content}
                  />
                  {i != benefits.length - 1 && <SizedBox height={16} />}
                </React.Fragment>
              );
            })}
          </BenefitsList>

          <SizedBox height={16} width={101} />

          <Carousel />
        </ContentLayout>
      </Layout>
    </Section>
  );
};

const BenefitBox: React.FC<{
  image: string;
  title: string;
  description: string;
}> = (props) => (
  <BenefitBoxWrapper>
    <BenefitImage
      src={`/insurers/customized-solutions/${props.image}`}
      alt={props.title}
    />
    <div>
      <BenefitTitle color={primaryColor}>{props.title}</BenefitTitle>
      <SizedBox height={8} />
      <Paragraph1>{props.description}</Paragraph1>
    </div>
  </BenefitBoxWrapper>
);

const Carousel: React.FC = () => (
  <SliderContainer>
    <Slider spaceBetween={0} slidesPerView={1} autoplayDelay={3000} loop>
      {Array.from({ length: 5 }).map((_, i) => (
        <ImageWrapper key={`screenshot-${i}`}>
          <Image src={`/insurers/customized-solutions/carousel-${i + 1}.jpg`} />
        </ImageWrapper>
      ))}
    </Slider>
  </SliderContainer>
);

const SliderContainer = styled.div`
  padding: 18px 12px;
`;

const Slider = styled(MySlider)`
  width: 512px;
`;

const ImageWrapper = styled.div`
  width: 512px;
  height: 475px;
  display: flex;
  align-items: center;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ContentLayout = styled(ResponsiveLayout)`
  ${mobileQuery`
    flex-direction: column-reverse;
  `}
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const BenefitBoxWrapper = styled(Card)`
  display: flex;
  flex-direction: row;
  width: 380px;
  box-sizing: border-box;
  ${mobileQuery`
    width: 100%;
  `}
`;

const BenefitImage = styled.img`
  width: 50px;
  height: 50px;
  margin-right: 16px;
`;

const BenefitTitle = styled(Paragraph1)`
  font-weight: bold;
`;
